/**
 * customer-rates — a customer's pricing fields, in ONE place (task #33).
 *
 * DEC-198 per-customer rates (per-BBL, hourly, bid/flat) plus the migration-0086
 * hourly billing clock. Two screens edit them: the full customer form
 * (OperatorFormModal) and the In Review "set this customer's rate" pop-up
 * (CustomerRatesDialog). Both render these fields and both run `parseRatesDraft`,
 * so there is one set of rules, not two copies.
 *
 * Money: dollars in the UI, integer cents on the wire, converted by the shared
 * exact parser (@lodex-pro/shared/money) — never `Number(x) * 100`.
 */

import * as React from 'react';
import {
  formatCentsAsDollars,
  hasSubCentPrecision,
  parseDollarsToCents,
} from '@lodex-pro/shared/money';
import { fieldStyle, labelStyle, FormRow } from './form-bits';
import type { HaulerOperator } from './hooks';

export type BillingStart = 'ARRIVAL' | 'START';
export type BillingEnd = 'DEPARTURE' | 'COMPLETION';

/** The fields as typed. '' = "no customer rate / use default". */
export interface RatesDraft {
  readonly perBbl: string;
  readonly hourly: string;
  readonly bid: string;
  readonly billingStart: '' | BillingStart;
  readonly billingEnd: '' | BillingEnd;
}

export const EMPTY_RATES: RatesDraft = {
  perBbl: '',
  hourly: '',
  bid: '',
  billingStart: '',
  billingEnd: '',
};

/** The PATCH body fields. null = clear the customer value so the default applies. */
export interface RatesPatch {
  readonly perBblPriceCents: number | null;
  readonly hourlyPriceCents: number | null;
  readonly bidPriceCents: number | null;
  readonly billingStart: BillingStart | null;
  readonly billingEnd: BillingEnd | null;
}

type RateSource = Pick<
  HaulerOperator,
  'perBblPriceCents' | 'hourlyPriceCents' | 'bidPriceCents' | 'billingStart' | 'billingEnd'
>;

const toDollars = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '' : formatCentsAsDollars(cents);

export function ratesDraftFrom(op: RateSource): RatesDraft {
  return {
    perBbl: toDollars(op.perBblPriceCents),
    hourly: toDollars(op.hourlyPriceCents),
    bid: toDollars(op.bidPriceCents),
    billingStart: op.billingStart ?? '',
    billingEnd: op.billingEnd ?? '',
  };
}

export const RATES_INVALID_MESSAGE =
  'Rates must be a dollar amount, e.g. 1.25. Leave a rate blank to use the default.';

/**
 * One rate box → cents. Blank is "no customer rate" (null); zero is a deliberate
 * free rate and is kept. Negative, a third decimal, or not-a-number is invalid.
 */
function rateCents(raw: string): number | null | undefined {
  const parsed = parseDollarsToCents(raw);
  if (!parsed.ok) return parsed.reason === 'blank' ? null : undefined;
  if (parsed.cents < 0 || hasSubCentPrecision(raw)) return undefined;
  return parsed.cents;
}

export function parseRatesDraft(
  d: RatesDraft,
):
  | { readonly ok: true; readonly rates: RatesPatch }
  | { readonly ok: false; readonly error: string } {
  const perBblPriceCents = rateCents(d.perBbl);
  const hourlyPriceCents = rateCents(d.hourly);
  const bidPriceCents = rateCents(d.bid);
  if (
    perBblPriceCents === undefined ||
    hourlyPriceCents === undefined ||
    bidPriceCents === undefined
  ) {
    return { ok: false, error: RATES_INVALID_MESSAGE };
  }
  return {
    ok: true,
    rates: {
      perBblPriceCents,
      hourlyPriceCents,
      bidPriceCents,
      billingStart: d.billingStart === '' ? null : d.billingStart,
      billingEnd: d.billingEnd === '' ? null : d.billingEnd,
    },
  };
}

/** The pricing section: three rate boxes and the hourly clock. */
export function CustomerRatesFields({
  value,
  onChange,
  idPrefix = 'ho',
}: {
  readonly value: RatesDraft;
  readonly onChange: (next: RatesDraft) => void;
  /** Keeps element ids unique when two forms can be mounted at once. */
  readonly idPrefix?: string;
}): React.JSX.Element {
  const set = <K extends keyof RatesDraft>(key: K, v: RatesDraft[K]): void =>
    onChange({ ...value, [key]: v });
  const hint: React.CSSProperties = {
    ...labelStyle,
    marginBottom: 0,
    textTransform: 'none',
    color: 'var(--color-text-tertiary)',
  };
  const rateBox = (key: 'perBbl' | 'hourly' | 'bid', label: string, placeholder: string) => (
    <FormRow label={label} htmlFor={`${idPrefix}-${key === 'perBbl' ? 'per-bbl' : key}`}>
      <input
        id={`${idPrefix}-${key === 'perBbl' ? 'per-bbl' : key}`}
        inputMode="decimal"
        value={value[key]}
        onChange={(e): void => set(key, e.target.value)}
        placeholder={placeholder}
        style={{ ...fieldStyle, maxWidth: '140px' }}
      />
    </FormRow>
  );
  return (
    <>
      <p style={{ ...labelStyle, marginBottom: 0 }}>Pricing</p>
      <p style={hint}>
        Used for any job type without its own price for this customer (set in Billing → Customer
        prices). Leave a rate blank to use this operator&apos;s or the job type&apos;s standard
        rate.
      </p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {rateBox('perBbl', 'Per BBL ($)', '1.25')}
        {rateBox('hourly', 'Hourly ($)', '95.00')}
        {rateBox('bid', 'Bid / flat ($)', '450.00')}
      </div>
      {/* Migration 0086 — the billing CLOCK, which decides how many hours an HOURLY job
          bills. Sits with pricing because it moves money the same way a rate does;
          "Use default" leaves it inheriting the operator/tenant. */}
      <p style={hint}>Hourly jobs bill from the start marker to the end marker, minus pauses.</p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <FormRow label="Hourly clock starts" htmlFor={`${idPrefix}-billing-start`}>
          <select
            id={`${idPrefix}-billing-start`}
            value={value.billingStart}
            onChange={(e): void =>
              set('billingStart', e.target.value as RatesDraft['billingStart'])
            }
            style={{ ...fieldStyle, maxWidth: '190px' }}
          >
            <option value="">Use default</option>
            <option value="ARRIVAL">Arrival at site</option>
            <option value="START">Driver starts the job</option>
          </select>
        </FormRow>
        <FormRow label="Hourly clock ends" htmlFor={`${idPrefix}-billing-end`}>
          <select
            id={`${idPrefix}-billing-end`}
            value={value.billingEnd}
            onChange={(e): void => set('billingEnd', e.target.value as RatesDraft['billingEnd'])}
            style={{ ...fieldStyle, maxWidth: '190px' }}
          >
            <option value="">Use default</option>
            <option value="DEPARTURE">Departure from disposal</option>
            <option value="COMPLETION">Job completion</option>
          </select>
        </FormRow>
      </div>
    </>
  );
}
